/**
 * Main Application
 * Initializes and coordinates all modules
 */

import ThemeManager from './theme.js';
import Gallery from './gallery.js';
import { getCategories, getSubcategories, getAllSubcategories, getPopularTags, syncGlobalStats } from './data.js';
import router from './router.js';
import wallpaperPage from './wallpaperPage.js';
import privacyPage from './privacyPage.js';
import aboutPage from './aboutPage.js';
import contactPage from './contactPage.js';
import { authManager } from './auth.js';
import { initAgeGate } from './ageGate.js';

const IS_NSFW = import.meta.env.VITE_NSFW === 'true';

function makeHeaderLink({ href, className, text, opacity = '1' }) {
    const link = document.createElement('a');
    link.href = href;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.className = `btn btn-secondary ${className}`;
    link.style.cssText = `font-size:0.8rem;padding:0.35rem 0.85rem;text-decoration:none;opacity:${opacity};`;
    link.textContent = text;
    return link;
}

function insertBeforeTheme(navActions, node) {
    const themeToggle = navActions.querySelector('.theme-toggle');
    if (themeToggle) navActions.insertBefore(node, themeToggle);
    else navActions.appendChild(node);
}

function setMeta(name, content, attr = 'name') {
    let tag = document.querySelector(`meta[${attr}="${name}"]`);
    if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
    }
    tag.setAttribute('content', content);
}

function setCanonical(path = '/') {
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
    }
    link.href = `https://anilora.com${path}`;
}

function slugifyTerm(value) {
    return String(value)
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/['’]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

class App {
    constructor() {
        this.themeManager = new ThemeManager();
        this.gallery = new Gallery('gallery');
        // Do not call init in constructor, it's async
    }

    hidePageLoader() {
        const loader = document.getElementById('pageLoader');
        if (!loader) return;
        loader.classList.add('is-hidden');
        window.setTimeout(() => loader.remove(), 320);
    }

    applyNsfwMode() {
        document.documentElement.setAttribute('data-site', 'nsfw');

        // NSFW site is light theme only — force it and hide the toggle
        this.themeManager.setTheme('dark');
        const themeToggle = document.querySelector('.theme-toggle');
        if (themeToggle) themeToggle.style.display = 'none';

        // Update page metadata
        document.title = 'Anilora NSFW — Explicit Adult Wallpapers';
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) metaDesc.setAttribute('content', 'Premium explicit adult wallpapers. Curated, uncensored, free to download.');

        // Swap hero text
        const heroTitle = document.querySelector('.hero-title');
        const heroSubtitle = document.querySelector('.hero-subtitle');
        if (heroTitle) heroTitle.textContent = 'Anilora After Dark';
        if (heroSubtitle) heroSubtitle.textContent =
            'Premium adult wallpapers — curated, explicit, free. No watermarks. No limits.';

        this.setFooterLandingLinks([
            {
                title: 'Categories',
                links: [
                    ['Anime Wallpapers', '/anime-wallpapers'],
                    ['Ecchi Wallpapers', '/ecchi-wallpapers'],
                    ['Real Wallpapers', '/real-wallpapers'],
                    ['NSFW Wallpapers', '/nsfw-wallpapers'],
                ],
            },
            {
                title: 'Popular',
                links: [
                    ['Anime Girl Wallpapers', '/anime-girl-wallpapers'],
                    ['Bikini Wallpapers', '/bikini-wallpapers'],
                    ['Neko Wallpapers', '/neko-wallpapers'],
                    ['Motorcycle Wallpapers', '/motorcycle-wallpapers'],
                ],
            },
        ]);

        // Swap logo label and add SFW link to header
        const logoSpan = document.querySelector('.logo span');
        if (logoSpan) logoSpan.textContent = 'Anilora';

        const navActions = document.querySelector('.nav-actions');
        if (navActions) {
            const sfwBtn = document.createElement('a');
            sfwBtn.href = 'https://anilora.com';
            sfwBtn.className = 'btn btn-secondary sfw-link';
            sfwBtn.style.cssText = 'font-size:0.8rem;padding:0.35rem 0.85rem;text-decoration:none;';
            sfwBtn.textContent = '← SFW';
            navActions.prepend(sfwBtn);

            insertBeforeTheme(navActions, makeHeaderLink({
                href: 'https://roleplay.anilora.com',
                className: 'roleplay-link',
                text: 'Roleplay',
            }));

            const genBtn = document.createElement('a');
            genBtn.href = 'https://generate.anilora.com';
            genBtn.target = '_blank';
            genBtn.rel = 'noopener noreferrer';
            genBtn.className = 'btn btn-secondary generate-link';
            genBtn.style.cssText = 'font-size:0.8rem;padding:0.35rem 0.85rem;text-decoration:none;';
            genBtn.textContent = 'Generate ✨';
            const ttNsfw = navActions.querySelector('.theme-toggle');
            if (ttNsfw) navActions.insertBefore(genBtn, ttNsfw);
            else navActions.appendChild(genBtn);
        }

        // --- Ads ---
        // Both sidebar and leaderboard use the global `atOptions` variable.
        // They must be injected sequentially — wait for sidebar invoke.js to
        // execute before setting atOptions for the leaderboard, otherwise the
        // second assignment overwrites the first before invoke.js reads it.

        // 1. Sidebar 160×600
        const sidebarSlots = [
            document.getElementById('adLeftSlot'),
            document.getElementById('adSidebarSlot'),
        ].filter(Boolean);
        sidebarSlots.forEach((sidebarSlot, index) => {
            const s1 = document.createElement('script');
            s1.text = `atOptions={'key':'34fb3917f359b8ca9de55bde2075acd9','format':'iframe','height':600,'width':160,'params':{}};`;
            const s2 = document.createElement('script');
            s2.src = 'https://www.highperformanceformat.com/34fb3917f359b8ca9de55bde2075acd9/invoke.js';
            // 2. Leaderboard 728×90 — injected only after sidebar invoke.js executes
            s2.onload = index === 0 ? () => {
                const leaderSlot = document.getElementById('adLeaderboardSlot');
                if (leaderSlot) {
                    leaderSlot.style.cssText = 'text-align:center;padding:0.5rem 0;width:100%;overflow:hidden;';
                    const s3 = document.createElement('script');
                    s3.text = `atOptions={'key':'097f5f8b3aad415e6aca1c9541dc8ba5','format':'iframe','height':90,'width':728,'params':{}};`;
                    const s4 = document.createElement('script');
                    s4.src = 'https://www.highperformanceformat.com/097f5f8b3aad415e6aca1c9541dc8ba5/invoke.js';
                    leaderSlot.appendChild(s3);
                    leaderSlot.appendChild(s4);
                }
            } : null;
            sidebarSlot.appendChild(s1);
            sidebarSlot.appendChild(s2);
        });

        // 3. Native banner (inside main content area — appended after gallery-ad-wrapper)
        const galleryWrapper = document.querySelector('.gallery-ad-wrapper');
        if (galleryWrapper) {
            const nativeWrap = document.createElement('div');
            nativeWrap.style.cssText = 'width:100%;margin:1.5rem 0;';
            const s5 = document.createElement('script');
            s5.async = true;
            s5.setAttribute('data-cfasync', 'false');
            s5.src = 'https://pl29325859.profitablecpmratenetwork.com/a8f4eb14a0acf5f0bec03f844281291c/invoke.js';
            const nativeDiv = document.createElement('div');
            nativeDiv.id = 'container-a8f4eb14a0acf5f0bec03f844281291c';
            nativeWrap.appendChild(s5);
            nativeWrap.appendChild(nativeDiv);
            galleryWrapper.after(nativeWrap);
        }

        // 4. Social bar (delayed 5s — above </body>)
        setTimeout(() => {
            const s6 = document.createElement('script');
            s6.src = 'https://pl29325860.profitablecpmratenetwork.com/17/f7/3f/17f73f1c12d6c53f182c604e51c05847.js';
            document.body.appendChild(s6);
        }, 5000);

        // Initialize age gate
        initAgeGate();
    }

    applyMainMode() {
        this.setHomeMeta();

        this.setFooterLandingLinks([
            {
                title: 'Categories',
                links: [
                    ['Anime Wallpapers', '/anime-wallpapers'],
                    ['Gaming Wallpapers', '/gaming-wallpapers'],
                    ['Aesthetic Wallpapers', '/aesthetic-wallpapers'],
                    ['Nature Wallpapers', '/nature-wallpapers'],
                ],
            },
            {
                title: 'Popular',
                links: [
                    ['Minecraft Wallpapers', '/minecraft-wallpapers'],
                    ['One Piece Wallpapers', '/one-piece-wallpapers'],
                    ['Anime Girl Wallpapers', '/anime-girl-wallpapers'],
                    ['Fantasy Wallpapers', '/fantasy-wallpapers'],
                ],
            },
        ]);

        // Add NSFW site link to main site header
        const navActions = document.querySelector('.nav-actions');
        if (navActions) {
            const nsfwBtn = document.createElement('a');
            nsfwBtn.href = 'https://nsfw.anilora.com';
            nsfwBtn.className = 'btn btn-secondary nsfw-link';
            nsfwBtn.style.cssText = 'font-size:0.8rem;padding:0.35rem 0.85rem;text-decoration:none;opacity:0.7;';
            nsfwBtn.setAttribute('rel', 'noopener noreferrer');
            nsfwBtn.setAttribute('target', '_blank');
            nsfwBtn.textContent = '🔞 18+';
            navActions.prepend(nsfwBtn);

            insertBeforeTheme(navActions, makeHeaderLink({
                href: 'https://roleplay.anilora.com',
                className: 'roleplay-link',
                text: 'Roleplay',
            }));

            const genBtn = document.createElement('a');
            genBtn.href = 'https://generate.anilora.com';
            genBtn.target = '_blank';
            genBtn.rel = 'noopener noreferrer';
            genBtn.className = 'btn btn-secondary generate-link';
            genBtn.style.cssText = 'font-size:0.8rem;padding:0.35rem 0.85rem;text-decoration:none;';
            genBtn.textContent = 'Generate ✨';
            const ttMain = navActions.querySelector('.theme-toggle');
            if (ttMain) navActions.insertBefore(genBtn, ttMain);
            else navActions.appendChild(genBtn);
        }

        // Sidebar 160×600 (main site — Adsterra)
        const sidebarSlots = [
            document.getElementById('adLeftSlot'),
            document.getElementById('adSidebarSlot'),
        ].filter(Boolean);
        sidebarSlots.forEach(sidebarSlot => {
            const s1 = document.createElement('script');
            s1.text = `atOptions={'key':'8f33f507458345b1f3f5c8885bc8ca62','format':'iframe','height':600,'width':160,'params':{}};`;
            const s2 = document.createElement('script');
            s2.src = 'https://www.highperformanceformat.com/8f33f507458345b1f3f5c8885bc8ca62/invoke.js';
            sidebarSlot.appendChild(s1);
            sidebarSlot.appendChild(s2);
        });

        // Social bar (Adsterra, delayed 5s)
        setTimeout(() => {
            const s = document.createElement('script');
            s.src = 'https://pl28760343.effectivegatecpm.com/f7/d0/1a/f7d01aae5650e07e14127927bdec1c7d.js';
            document.head.appendChild(s);
        }, 5000);
    }

    setFooterLandingLinks(sections) {
        const footerSections = document.querySelectorAll('.footer-section');
        sections.forEach((section, index) => {
            const container = footerSections[index + 1];
            if (!container) return;
            const heading = container.querySelector('h4');
            const links = container.querySelector('.footer-links');
            if (heading) heading.textContent = section.title;
            if (links) {
                links.innerHTML = section.links
                    .map(([label, href]) => `<a href="${href}" class="footer-link">${label}</a>`)
                    .join('');
            }
        });
    }

    setHomeMeta() {
        document.title = 'Anilora - Free Anime, Gaming & Aesthetic Wallpapers';
        setMeta('description', 'Download free high-quality anime, gaming, fantasy, nature, and aesthetic wallpapers for desktop and mobile. Curated 4K wallpapers with no watermarks.');
        setMeta('robots', 'index, follow, max-image-preview:large');
        setMeta('og:type', 'website', 'property');
        setMeta('og:title', 'Anilora - Free Anime, Gaming & Aesthetic Wallpapers', 'property');
        setMeta('og:description', 'Download free high-quality anime, gaming, fantasy, nature, and aesthetic wallpapers for desktop and mobile.', 'property');
        setMeta('og:url', 'https://anilora.com/', 'property');
        setMeta('og:image', 'https://anilora.com/logo.png', 'property');
        setMeta('twitter:title', 'Anilora - Free Anime, Gaming & Aesthetic Wallpapers');
        setMeta('twitter:description', 'Download free high-quality wallpapers for desktop and mobile.');
        setMeta('twitter:image', 'https://anilora.com/logo.png');
        setCanonical('/');
    }

    setLandingMeta(label, path) {
        const modePrefix = IS_NSFW ? 'Adult ' : '';
        const title = `${modePrefix}${label} Wallpapers - Free Download | Anilora`;
        const description = IS_NSFW
            ? `Browse free adult ${label.toLowerCase()} wallpapers for desktop and mobile. Curated mature wallpapers with no watermarks.`
            : `Browse free ${label.toLowerCase()} wallpapers for desktop and mobile. Curated high-quality wallpapers with no watermarks.`;
        const domain = IS_NSFW ? 'https://nsfw.anilora.com' : 'https://anilora.com';

        document.title = title;
        setMeta('description', description);
        setMeta('robots', 'index, follow, max-image-preview:large');
        setMeta('og:type', 'website', 'property');
        setMeta('og:title', title, 'property');
        setMeta('og:description', description, 'property');
        setMeta('og:url', `${domain}${path}`, 'property');
        setMeta('og:image', `${domain}/logo.png`, 'property');
        setMeta('twitter:title', title);
        setMeta('twitter:description', description);
        setMeta('twitter:image', `${domain}/logo.png`);
        setCanonical(path);
    }

    async init() {
        try {
            // Apply site mode before rendering anything
            if (IS_NSFW) {
                this.applyNsfwMode();
            } else {
                this.applyMainMode();
            }

            // Initialize anonymous authentication for Supabase features
            await authManager.init();

            // Start syncing statistics from Supabase in the background
            // We don't 'await' it here so the gallery renders immediately
            syncGlobalStats().then(() => {
                this.gallery.updateGallery(); // Refresh gallery once stats are in
            });

            this.setupThemeToggle();
            this.setupSidebar();
            this.setupSearch();
            this.setupCategoryFilters();
            this.setupFooterLinks();
            this.setupTagFiltering();
            this.setupSortControls();

            // Set up routes before initializing router
            this.setupRouter();

            // Update gallery immediately with local data
            this.gallery.updateGallery();
            this.hidePageLoader();
        } catch (error) {
            console.error('Failed to initialize app:', error);
            // Fallback: render gallery anyway
            this.gallery.updateGallery();
            this.hidePageLoader();
        }
    }

    hideAllPages() {
        wallpaperPage.hide();
        privacyPage.hide();
        aboutPage.hide();
        contactPage.hide();
    }

    setupRouter() {
        // Define routes
        router.on('/', () => {
            this.setHomeMeta();
            this.hideAllPages();
            this.gallery.updateGallery();
        });

        router.on('/wallpaper/:id', ({ params }) => {
            this.hideAllPages();
            wallpaperPage.render(params.id);
        });

        router.on('/privacy', () => {
            this.hideAllPages();
            privacyPage.render();
        });

        router.on('/about', () => {
            this.hideAllPages();
            aboutPage.render();
        });

        router.on('/contact', () => {
            this.hideAllPages();
            contactPage.render();
        });

        this.setupLandingRoutes();

        // Initialize router with current URL (do this last)
        // Use setTimeout to ensure DOM is fully ready
        setTimeout(() => {
            router.init();
        }, 100);
    }

    setupLandingRoutes() {
        const registered = new Set();
        const register = (path, label, handler) => {
            if (registered.has(path)) return;
            registered.add(path);
            const routeHandler = () => {
                this.hideAllPages();
                this.setLandingMeta(label, path);
                handler();
            };
            router.on(path, routeHandler);
            router.on(`${path}/`, routeHandler);
        };

        getCategories().forEach(category => {
            register(`/${slugifyTerm(category.name)}-wallpapers`, `${category.name}`, () => {
                this.gallery.filterByCategory(category.slug);
            });
        });

        getAllSubcategories().forEach(subcategory => {
            register(`/${slugifyTerm(subcategory.name)}-wallpapers`, subcategory.name, () => {
                this.gallery.filterBySubcategory(subcategory.category.toLowerCase(), subcategory.slug);
            });
        });

        getPopularTags(IS_NSFW ? 24 : 30)
            .filter(tag => tag.count >= 2)
            .forEach(({ tag }) => {
                const label = tag.replace(/\b\w/g, char => char.toUpperCase());
                register(`/${slugifyTerm(tag)}-wallpapers`, label, () => {
                    const searchInput = document.getElementById('searchInput');
                    const sidebarSearchInput = document.getElementById('sidebarSearchInput');
                    if (searchInput) searchInput.value = tag;
                    if (sidebarSearchInput) sidebarSearchInput.value = tag;
                    this.gallery.search(tag);
                });
            });
    }

    setupSidebar() {
        const sidebar = document.getElementById('sidebar');
        const sidebarToggle = document.getElementById('sidebarToggle');
        const sidebarClose = document.getElementById('sidebarClose');
        const sidebarOverlay = document.getElementById('sidebarOverlay');

        const toggleSidebar = () => {
            if (!sidebar || !sidebarOverlay) return;
            sidebar.classList.toggle('active');
            sidebarOverlay.classList.toggle('active');
            document.body.style.overflow = sidebar.classList.contains('active') ? 'hidden' : '';
        };

        if (sidebarToggle) sidebarToggle.addEventListener('click', toggleSidebar);
        if (sidebarClose) sidebarClose.addEventListener('click', toggleSidebar);
        if (sidebarOverlay) sidebarOverlay.addEventListener('click', toggleSidebar);
    }

    setupTagFiltering() {
        window.addEventListener('filterByTag', (e) => {
            const tag = e.detail.tag;
            // Update search input with tag
            const searchInput = document.getElementById('searchInput');
            if (searchInput) {
                searchInput.value = tag;
                this.gallery.search(tag);
            }
        });
    }

    setupThemeToggle() {
        const themeToggle = document.querySelector('.theme-toggle');
        if (themeToggle) {
            themeToggle.addEventListener('click', () => {
                this.themeManager.toggleTheme();
            });
            this.themeManager.updateToggleButton();
        }
    }

    setupSearch() {
        const searchInput = document.getElementById('searchInput');
        const sidebarSearchInput = document.getElementById('sidebarSearchInput');

        const handleSearch = (e) => {
            const value = e.target.value;
            if (searchInput) searchInput.value = value;
            if (sidebarSearchInput) sidebarSearchInput.value = value;

            clearTimeout(this.searchDebounce);
            this.searchDebounce = setTimeout(() => {
                this.gallery.search(value);
            }, 300);
        };

        if (searchInput) searchInput.addEventListener('input', handleSearch);
        if (sidebarSearchInput) sidebarSearchInput.addEventListener('input', handleSearch);
    }

    setupCategoryFilters() {
        const categoryFilter = document.getElementById('categoryFilter');
        const sidebarCategories = document.getElementById('sidebarCategories');
        if (!categoryFilter) return;

        const categories = [{ name: 'All', slug: 'all', count: 0 }, ...getCategories()];

        const html = categories.map(category => {
            const subcategories = category.slug !== 'all' ? getSubcategories(category.slug) : [];

            return `
        <div class="category-group">
          <button class="category-pill ${category.slug === 'all' ? 'active' : ''}"
                  data-category="${category.slug}"
                  data-has-subcategories="${subcategories.length > 0}">
            ${category.name}
            ${category.count > 0 ? `<span style="opacity: 0.6; margin-left: 0.25rem;">(${category.count})</span>` : ''}
            ${subcategories.length > 0 ? '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-left: 0.25rem;"><polyline points="6 9 12 15 18 9"></polyline></svg>' : ''}
          </button>
          ${subcategories.length > 0 ? `
            <div class="subcategory-dropdown" data-parent="${category.slug}">
              ${subcategories.map(sub => `
                <button class="subcategory-pill"
                        data-category="${category.slug}"
                        data-subcategory="${sub.slug}">
                  ${sub.name}
                  <span style="opacity: 0.6; margin-left: 0.25rem;">(${sub.count})</span>
                </button>
              `).join('')}
            </div>
          ` : ''}
        </div>
      `;
        }).join('');

        categoryFilter.innerHTML = html;

        // Render sidebar category list
        if (sidebarCategories) {
            sidebarCategories.innerHTML = categories.map(category => `
                <div class="sidebar-category-item ${category.slug === 'all' ? 'active' : ''}" data-category="${category.slug}">
                    ${category.name}
                </div>
            `).join('');
        }

        const handleCategoryClick = (category, element) => {
            // Update active state for both main and sidebar pills
            document.querySelectorAll(`.category-pill, .sidebar-category-item`).forEach(p => p.classList.remove('active'));
            document.querySelectorAll(`.category-pill[data-category="${category}"], .sidebar-category-item[data-category="${category}"]`).forEach(p => p.classList.add('active'));

            this.gallery.filterByCategory(category);

            // If clicked from sidebar, close it
            if (element.classList.contains('sidebar-category-item')) {
                const sidebar = document.getElementById('sidebar');
                const sidebarOverlay = document.getElementById('sidebarOverlay');
                if (sidebar) sidebar.classList.remove('active');
                if (sidebarOverlay) sidebarOverlay.classList.remove('active');
                document.body.style.overflow = '';
            }
        };

        // Click listeners for both sets of category buttons
        document.querySelectorAll('.category-pill, .sidebar-category-item').forEach(pill => {
            pill.addEventListener('click', (e) => {
                const category = e.currentTarget.dataset.category;
                const hasSubcategories = e.currentTarget.dataset.hasSubcategories === 'true';

                // Handle subcategory toggle on main pill
                if (hasSubcategories && e.currentTarget.classList.contains('category-pill')) {
                    const dropdown = e.currentTarget.parentElement.querySelector('.subcategory-dropdown');
                    const isOpen = dropdown.classList.contains('open');

                    categoryFilter.querySelectorAll('.subcategory-dropdown').forEach(d => d.classList.remove('open'));
                    categoryFilter.querySelectorAll('.category-pill').forEach(p => p.classList.remove('dropdown-open'));

                    if (!isOpen) {
                        dropdown.classList.add('open');
                        e.currentTarget.classList.add('dropdown-open');
                    }
                }

                handleCategoryClick(category, e.currentTarget);
            });
        });

        // Click listeners for subcategories (only in main layout)
        categoryFilter.querySelectorAll('.subcategory-pill').forEach(pill => {
            pill.addEventListener('click', (e) => {
                const category = e.currentTarget.dataset.category;
                const subcategory = e.currentTarget.dataset.subcategory;

                document.querySelectorAll('.category-pill, .subcategory-pill, .sidebar-category-item').forEach(p => p.classList.remove('active'));
                e.currentTarget.classList.add('active');

                this.gallery.filterBySubcategory(category, subcategory);
            });
        });

        // Close dropdowns when clicking outside
        document.addEventListener('click', (e) => {
            if (!e.target.closest('.category-group')) {
                categoryFilter.querySelectorAll('.subcategory-dropdown').forEach(d => d.classList.remove('open'));
                categoryFilter.querySelectorAll('.category-pill').forEach(p => p.classList.remove('dropdown-open'));
            }
        });
    }

    setupFooterLinks() {
        const footerLinks = document.querySelectorAll('.footer-link[data-category]');
        footerLinks.forEach(link => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                const category = link.dataset.category;

                // Find the corresponding category pill and click it
                const pill = document.querySelector(`.category-pill[data-category="${category}"]`);
                if (pill) {
                    pill.click();

                    // Scroll to gallery
                    const gallerySection = document.getElementById('categoryFilter');
                    if (gallerySection) {
                        gallerySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }
            });
        });

        // Page links (About, Privacy, Contact)
        const pageLinks = document.querySelectorAll('.footer-link[data-route]');
        pageLinks.forEach(link => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                router.navigate(link.dataset.route);
            });
        });
    }

    setupSortControls() {
        const sortSelect = document.getElementById('sortSelect');
        const sidebarSortSelect = document.getElementById('sidebarSortSelect');
        const shuffleBtn = document.getElementById('shuffleBtn');
        const sidebarShuffleBtn = document.getElementById('sidebarShuffleBtn');
        const shuffleBtns = [shuffleBtn, sidebarShuffleBtn].filter(Boolean);

        const setShuffleActive = (active) => {
            shuffleBtns.forEach(btn => {
                btn.classList.toggle('active', active);
                btn.setAttribute('aria-pressed', String(active));
            });
        };

        const handleSort = (e) => {
            const value = e.target.value;
            if (sortSelect) sortSelect.value = value;
            if (sidebarSortSelect) sidebarSortSelect.value = value;
            setShuffleActive(false);
            this.gallery.setSort(value);
        };

        const handleShuffle = () => {
            setShuffleActive(true);
            this.gallery.shuffle();
        };

        if (sortSelect) sortSelect.addEventListener('change', handleSort);
        if (sidebarSortSelect) sidebarSortSelect.addEventListener('change', handleSort);
        shuffleBtns.forEach(btn => btn.addEventListener('click', handleShuffle));
    }
}

// Initialize app when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', async () => {
        const app = new App();
        await app.init();
    });
} else {
    (async () => {
        const app = new App();
        await app.init();
    })();
}

window.setTimeout(() => {
    const loader = document.getElementById('pageLoader');
    if (loader) loader.classList.add('is-hidden');
}, 3500);
